/**
 * /review — list of finished + in-progress tournaments from Dexie.
 * Click a row to step into `/review/:tournamentId`. HH `.txt` can be
 * imported via drag-drop / file picker at the top.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HhFileDropzone, type ImportResult } from '@/components/review/HhFileDropzone';
import type { TournamentRow } from '@/storage/db';
import { listTournaments } from '@/storage/handHistory';

export function ReviewRoute() {
  const [rows, setRows] = useState<TournamentRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const tournaments = await listTournaments();
    setRows(tournaments);
    setLoaded(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await refresh();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const handleImport = useCallback(
    (results: ReadonlyArray<ImportResult>) => {
      const ok = results.filter((r) => r.kind === 'ok').length;
      const fail = results.length - ok;
      if (ok > 0) {
        void refresh();
      }
      const firstErr = results.find(
        (r): r is Extract<ImportResult, { kind: 'error' }> => r.kind === 'error',
      );
      const msg = [
        ok > 0 ? `토너먼트 ${ok}개 가져옴` : null,
        fail > 0 && firstErr ? `실패 ${fail}: ${firstErr.message}` : null,
      ]
        .filter((x): x is string => x !== null)
        .join(' · ');
      setToast(msg || null);
      if (msg) {
        window.setTimeout(() => setToast(null), 4000);
      }
    },
    [refresh],
  );

  return (
    <main className="min-h-dvh bg-bg text-fg flex flex-col gap-4 p-4" data-testid="review-route">
      <header className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">리뷰</h1>
        <Link to="/" className="text-xs text-accent underline">
          ← 홈
        </Link>
      </header>
      <HhFileDropzone onResults={handleImport} />
      {toast ? (
        <div
          className="rounded-md bg-bg-elev border border-border px-3 py-2 text-xs text-fg"
          data-testid="import-toast"
        >
          {toast}
        </div>
      ) : null}
      {!loaded ? (
        <p className="text-sm text-muted">불러오는 중…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted" data-testid="review-empty">
          저장된 토너먼트가 아직 없습니다. /play에서 한 판 플레이 해보세요.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="review-list">
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-md border border-border bg-bg-elev px-3 py-2 flex items-center justify-between"
            >
              <div className="flex flex-col">
                <span className="font-mono text-sm">{row.id}</span>
                <span className="text-[10px] text-muted">
                  시작 {new Date(row.startedAt).toLocaleString()} · 종료{' '}
                  {row.endedAt ? new Date(row.endedAt).toLocaleString() : '진행 중'}
                </span>
              </div>
              <Link
                to={`/review/${encodeURIComponent(row.id)}`}
                className="text-xs text-accent underline"
              >
                상세 →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
